import React, { useEffect, useRef, useState, useCallback } from "react";
import { firebaseApp } from "firebase/client";
import { UserRoles } from "model/dictionary";

export const AuthContext = React.createContext();

const ROLE_KEYS = Object.keys(UserRoles);

const pickRoleClaims = (claims = {}) => {
  const roles = {};
  ROLE_KEYS.forEach((key) => {
    if (typeof claims[key] === "boolean") {
      roles[key] = claims[key];
    }
  });
  return roles;
};

const cleanEmailLinkFromUrl = () => {
  window.history.replaceState(
    {},
    "",
    window.location.origin + window.location.pathname
  );
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userLogged, setUserLogged] = useState(false);
  const [currentUserRoles, setCurrentUserRoles] = useState(UserRoles);
  const [loading, setLoading] = useState(true);
  const emailLinkHandled = useRef(false);

  const signInAnonymous = useCallback(() => {
    if (!userLogged) {
      firebaseApp
        .auth()
        .signInAnonymously()
        .finally(() => {
          setLoading(false);
        });
    }
  }, [userLogged]);

  // Email-link sign-in: se procesa una sola vez al montar. Limpiamos la URL
  // y el localStorage en cualquier desenlace para que un F5 sobre la URL del
  // link no reintente con un oobCode ya consumido (causaba 400 en consola).
  useEffect(() => {
    if (emailLinkHandled.current) return;
    emailLinkHandled.current = true;

    const auth = firebaseApp.auth();
    if (!auth.isSignInWithEmailLink(window.location.href)) return;

    const email = window.localStorage.getItem("emailForSignIn");
    if (email === null) {
      cleanEmailLinkFromUrl();
      return;
    }

    auth
      .signInWithEmailLink(email, window.location.href)
      .catch((error) => {
        console.error("signInWithEmailLink falló:", error);
      })
      .finally(() => {
        window.localStorage.removeItem("emailForSignIn");
        cleanEmailLinkFromUrl();
      });
  }, []);

  useEffect(() => {
    let unsubscribe = firebaseApp.auth().onAuthStateChanged((user) => {
      setUserLogged(user && !user.isAnonymous);
      setCurrentUser(user);

      if (!user) {
        setLoading(true);
        setCurrentUserRoles(UserRoles);
        signInAnonymous();
      } else if (user.isAnonymous) {
        setCurrentUser(null);
        setCurrentUserRoles(UserRoles);
        setLoading(false);
      } else {
        setLoading(true);
        user
          .getIdTokenResult()
          .then((result) => {
            const cached = pickRoleClaims(result.claims);
            if (Object.keys(cached).length > 0) {
              setCurrentUserRoles({ ...UserRoles, ...cached });
              return null;
            }
            // El JWT cacheado no trae claims de rol — token stale o claims recién seteados.
            // Forzar refresh contra Firebase Auth para traer los claims actuales.
            return user.getIdTokenResult(true).then((fresh) => {
              setCurrentUserRoles({ ...UserRoles, ...pickRoleClaims(fresh.claims) });
            });
          })
          .catch((error) => {
            console.log(error);
            setCurrentUserRoles(UserRoles);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [setCurrentUserRoles, signInAnonymous]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        currentUserRoles,
        userLogged,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
